/* eslint-disable */
/*
 * avi-surgeon.js — rebuild the header of an AVI whose own header is gone.
 *
 * WHY THIS EXISTS
 * ---------------
 * An AVI keeps everything that makes it playable in the first few kilobytes:
 * `hdrl` names each stream's codec, frame rate, sample rate and picture size.
 * The frames themselves sit behind it in `movi` as `00dc` / `01wb` chunks that
 * carry NO timing at all. Lose the first cluster of the file — a camera that
 * dies before rewriting the header, a card whose FAT points the first cluster
 * somewhere else, a recovery tool that carves from the wrong sector — and
 * FFmpeg answers "Invalid data found when processing input" to a file whose
 * every frame is still there. /repair-video used to call such files empty
 * (until 2026-09-25 the locator had no AVI anchor below the header): over the
 * 90 days before that, 107 sessions brought an .avi that got the verdict
 * "no video in this file".
 *
 * WHAT HAS TO BE LEARNED, AND FROM WHERE
 * --------------------------------------
 * Matroska blocks carry their own timestamps, so its header only has to be
 * DECLARED (matroska-surgeon.js). AVI chunks carry none, so three things have
 * to be recovered from the bytes, and each one was measured before it was
 * trusted (27 real-world AVIs from samples.ffmpeg.org — Canon, Casio, Fuji,
 * Nikon, Pentax, Samsung and no-name cameras, DV, Xvid, H.264 — plus an
 * encoded matrix with damaged heads):
 *
 *   codec     the frames name themselves: JPEG SOI/SOF, H.264 SPS, MPEG-4 VOL,
 *             DV DIF headers; MPEG audio and AC-3 frame chains; IMA / MS ADPCM
 *             block headers; PCM layout by PcmLayoutGuess, restricted to what an
 *             AVI can hold (little-endian, 1–2 channels, u8/s16/s24/f32/G.711).
 *
 *   rates     camera frame rates are NOT a standard list — measured 11.73 fps
 *             (Casio), 13.24 (no-name), 20 (Canon), 24 (Nikon) — and Canon's
 *             audio clock is 11 024 Hz. What IS exact is the interleave: audio
 *             samples per video frame. Many cameras write exactly one second of
 *             audio per chunk (Canon, Casio, Fuji, Pentax, Nikon), which pins the
 *             sample rate outright; otherwise a prior over sample rates × a prior
 *             over frame rates picks the pair, and a near tie is REPORTED as
 *             ambiguous so the page can offer the alternatives. A donor clip from
 *             the same camera answers exactly (`reference`).
 *
 *   sync      where the audio starts relative to the first surviving frame.
 *             Taking "the audio chunk after the first frame" is off by 350–630 ms
 *             median (p90 ≈ 1 s) on every camera that writes one-second chunks.
 *             Two rules measured exact: "both streams end together" (0 ms on
 *             Canon/Fuji/Pentax/Nikon, on the Casio that stores ALL its audio
 *             after the video, and on MEncoder files with a 0.5 s preload) — but
 *             only when the tail survived (idx1 is there); with a truncated tail
 *             it drifts 300–570 ms, and the interleave regression (audio time vs.
 *             video frames per chunk, writer offset 0) stays at 0–1 ms instead.
 *
 * NOTHING HERE DECIDES WHETHER THE RESULT IS GOOD. `rebuild()` returns a
 * candidate; the caller decodes it, exactly as repair-video does for every other
 * lane. A header that describes the frames wrongly produces a file that fails
 * that check, which is the honest way to find out.
 *
 * Public API
 *   AviSurgeon.walk(file, {at})                 → chunk table (what is where)
 *   AviSurgeon.examine(file, {at, pcmGuess})    → codecs, rates, sync, key start
 *   AviSurgeon.rebuild(file, opts)              → {blob, video, audio, rates, …}
 *                                                 | {blob:null, needsDonor:'h264-params'|'mpeg4-vol'|'video-codec', …}
 *                                                 | null (no chunk chain at all)
 *   AviSurgeon.bodyStart(file, {at})            → where a RIFF AVI's chunks begin when its header is broken, or -1
 *   AviSurgeon.headerFrom(referenceFile)        → donor streams | null
 *   AviSurgeon.solveRates(input)                → the rate solver, pure (tests)
 *
 * rebuild opts: {at, pcmGuess, reference, fps, sampleRate, onProgress, signal}.
 * `rates.ambiguous` + `rates.alternatives` say when the frame rate is a guess
 * (MJPEG without sound carries no timing at all): the page offers the others.
 */

var MB = 1048576;
var WINDOW = 8 * MB;
var MAX_CHUNK = 64 * MB;          // 4K MJPEG frames stay far below; anything bigger is garbage
var HEAD = 256;                   // bytes of each video chunk the keyframe decision reads
var SLOP = 32;                    // how far a writer's size field may be off before we call it a hole
var K_VIDEO = 1, K_AUDIO = 2, K_TEXT = 3, K_PAL = 4, K_DV = 5;
var S_LIST = 1, S_RIFF = 2, S_SKIP = 3, S_IDX1 = 4;
var MAX_RIFF_BYTES = 0xFFF00000;  // above this the 32-bit RIFF/movi sizes cannot say it

/* ── byte helpers ─────────────────────────────────────────────────────────── */

function u32(b, i) { return (b[i] | (b[i + 1] << 8) | (b[i + 2] << 16) | (b[i + 3] << 24)) >>> 0; }
function u16(b, i) { return b[i] | (b[i + 1] << 8); }
function s16(b, i) { var v = b[i] | (b[i + 1] << 8); return v > 32767 ? v - 65536 : v; }
function isDigit(x) { return x >= 0x30 && x <= 0x39; }
function fcc(b, i) { return String.fromCharCode(b[i], b[i + 1], b[i + 2], b[i + 3]); }

async function readAt(file, at, len) {
    var end = Math.min(at + len, file.size);
    if (end <= at) return new Uint8Array(0);
    return new Uint8Array(await file.slice(at, end).arrayBuffer());
}

function concat(parts) {
    var n = 0, i;
    for (i = 0; i < parts.length; i++) n += parts[i].length;
    var out = new Uint8Array(n), o = 0;
    for (i = 0; i < parts.length; i++) { out.set(parts[i], o); o += parts[i].length; }
    return out;
}

/* ── chunk ids ────────────────────────────────────────────────────────────── */

/** Stream chunk kind at `i` ('00dc', '01wb', '00__' …), 0 when it is not one. */
function streamKind(b, i) {
    if (!isDigit(b[i]) || !isDigit(b[i + 1])) return 0;
    var t0 = b[i + 2], t1 = b[i + 3];
    if (t0 === 0x64 && (t1 === 0x63 || t1 === 0x62)) return K_VIDEO;   // dc, db
    if (t0 === 0x77 && t1 === 0x62) return K_AUDIO;                     // wb
    if (t0 === 0x5f && t1 === 0x5f) return K_DV;                        // __ — DV type 1 ('iavs')
    if (t0 === 0x74 && t1 === 0x78) return K_TEXT;                      // tx
    if (t0 === 0x70 && t1 === 0x63) return K_PAL;                       // pc — palette change
    return 0;
}
function sidOf(b, i) { return (b[i] - 0x30) * 10 + (b[i + 1] - 0x30); }

/** RIFF structure that may sit between stream chunks. */
function structKind(b, i) {
    var a = b[i], c = b[i + 1], d = b[i + 2], e = b[i + 3];
    if (a === 0x4c && c === 0x49 && d === 0x53 && e === 0x54) return S_LIST;
    if (a === 0x52 && c === 0x49 && d === 0x46 && e === 0x46) return S_RIFF;
    if (a === 0x4a && c === 0x55 && d === 0x4e && e === 0x4b) return S_SKIP;   // JUNK
    if (a === 0x69 && c === 0x64 && d === 0x78 && e === 0x31) return S_IDX1;
    if (a === 0x69 && c === 0x78 && isDigit(d) && isDigit(e)) return S_SKIP;   // ix## (OpenDML)
    return 0;
}

/**
 * How many links does the chain at `i` hold inside `b`? Stream chunks and the
 * structure that may sit between them, each at the offset the previous size
 * says. `sids` (optional) — after a hole, only the streams this file already
 * has count, so the next file on a card cannot pass for our continuation.
 */
function chainLen(b, i, n, need, sids) {
    var p = i, links = 0, av = 0;
    while (p + 12 <= n && links < need) {
        var k = streamKind(b, p), sz;
        if (k) {
            if (sids && !sids[sidOf(b, p)]) break;
            sz = u32(b, p + 4);
            if (sz > MAX_CHUNK) break;
            links++; av++;
            p += 8 + sz + (sz & 1);
            continue;
        }
        var s = structKind(b, p);
        if (!s) break;
        sz = u32(b, p + 4);
        if (s === S_LIST || s === S_RIFF) {
            var t = fcc(b, p + 8);
            if (t === 'movi' || t === 'rec ' || t === 'AVIX') { links++; p += 12; continue; }
        }
        if (sz > MAX_CHUNK) break;
        links++;
        p += 8 + sz + (sz & 1);
    }
    return av ? links : 0;
}

/* ── the chunk table ─────────────────────────────────────────────────────── */

function Table() {
    this.n = 0; this.cap = 4096;
    this.off = new Float64Array(this.cap);
    this.size = new Uint32Array(this.cap);
    this.sid = new Uint8Array(this.cap);
    this.kind = new Uint8Array(this.cap);
    this.key = new Uint8Array(this.cap);      // 1 key · 0 not · 2 undecided
    this.seg = new Uint32Array(this.cap);     // bumps at every hole / slop: contiguity breaks
}
Table.prototype.push = function (off, size, sid, kind, key, seg) {
    if (this.n === this.cap) {
        var c = this.cap * 2, t = this;
        ['off', 'size', 'sid', 'kind', 'key', 'seg'].forEach(function (f) {
            var a = new t[f].constructor(c); a.set(t[f]); t[f] = a;
        });
        this.cap = c;
    }
    var i = this.n++;
    this.off[i] = off; this.size[i] = size; this.sid[i] = sid;
    this.kind[i] = kind; this.key[i] = key; this.seg[i] = seg;
    return i;
};

/* ── video: what the frames say about themselves ─────────────────────────── */

/** Codec family from the first bytes of a frame. */
function familyOf(h) {
    if (h.length >= 3 && h[0] === 0xff && h[1] === 0xd8 && h[2] === 0xff) return 'mjpeg';
    if (h.length >= 4 && h[0] === 0x1f && h[1] === 0x07 && h[2] === 0x00) return 'dv';
    var n = Math.min(h.length, HEAD);
    for (var i = 0; i + 4 < n; i++) {
        if (h[i] !== 0 || h[i + 1] !== 0 || h[i + 2] !== 1) continue;
        var c = h[i + 3];
        if (c === 0xb0 || c === 0xb5 || c === 0xb6 || (c >= 0x20 && c <= 0x2f)) return 'mpeg4';
        if (c === 0xb3 && i + 12 < n && ((h[i + 7] & 0x0f) >= 1 && (h[i + 7] & 0x0f) <= 8)
            && (h[i + 10] & 0x20)) return 'mpeg2';
        if (c === 0x40 && h[i + 4] === 0x01) return 'hevc';
        var t = c & 0x1f;
        if (!(c & 0x80) && (t === 7 || t === 5 || t === 1 || t === 9 || t === 6)) return 'h264';
    }
    /* Length-prefixed H.264 (AVCC): the decoder configuration lived only in the
       header that is gone — the donor is the answer. */
    if (h.length >= 5) {
        var len = ((h[0] << 24) | (h[1] << 16) | (h[2] << 8) | h[3]) >>> 0;
        var nal = h[4];
        if (len > 0 && len < 64 * MB && !(nal & 0x80) && [1, 5, 6, 7, 9].indexOf(nal & 0x1f) >= 0) return 'h264-avcc';
    }
    return null;
}

/** Is this frame a keyframe, for a known family? Reads only its head. */
function isKey(family, h) {
    var n = Math.min(h.length, HEAD), i;
    if (family === 'mjpeg' || family === 'dv') return 1;
    if (family === 'h264') {
        for (i = 0; i + 3 < n; i++) {
            if (h[i] === 0 && h[i + 1] === 0 && h[i + 2] === 1) {
                var t = h[i + 3] & 0x1f;
                if (t === 5 || t === 7) return 1;
                if (t === 1) return 0;
            }
        }
        return 0;
    }
    if (family === 'mpeg4') {
        for (i = 0; i + 4 < n; i++) {
            if (h[i] === 0 && h[i + 1] === 0 && h[i + 2] === 1 && h[i + 3] === 0xb6) return (h[i + 4] >> 6) === 0 ? 1 : 0;
        }
        return 0;
    }
    if (family === 'mpeg2') {
        for (i = 0; i + 5 < n; i++) {
            if (h[i] === 0 && h[i + 1] === 0 && h[i + 2] === 1 && h[i + 3] === 0x00) return ((h[i + 5] >> 3) & 7) === 1 ? 1 : 0;
        }
        return 0;
    }
    return 0;
}

/** MSB-first bit reader; `strip` removes H.264 emulation-prevention bytes. */
function Bits(b, strip) {
    if (strip) {
        var out = new Uint8Array(b.length), o = 0, z = 0;
        for (var i = 0; i < b.length; i++) {
            if (z >= 2 && b[i] === 3) { z = 0; continue; }
            z = b[i] === 0 ? z + 1 : 0;
            out[o++] = b[i];
        }
        b = out.subarray(0, o);
    }
    this.b = b; this.i = 0;
}
Bits.prototype.u = function (n) {
    var v = 0;
    for (var k = 0; k < n; k++) {
        var byte = this.b[this.i >> 3];
        if (byte === undefined) throw new Error('eof');
        v = v * 2 + ((byte >> (7 - (this.i & 7))) & 1); this.i++;
    }
    return v;
};
Bits.prototype.ue = function () {
    var z = 0;
    while (this.u(1) === 0) { if (++z > 31) throw new Error('ue'); }
    return Math.pow(2, z) - 1 + this.u(z);
};
Bits.prototype.se = function () { var k = this.ue(); return k & 1 ? (k + 1) / 2 : -(k / 2); };

/** H.264 SPS → { width, height, fps|null }. `nal` starts at the NAL header byte. */
function h264Sps(nal) {
    var b = new Bits(nal.subarray(1, Math.min(nal.length, 512)), true);
    var prof = b.u(8); b.u(8); b.u(8); b.ue();
    var cf = 1;
    if ([100, 110, 122, 244, 44, 83, 86, 118, 128, 138, 139, 134, 135].indexOf(prof) >= 0) {
        cf = b.ue();
        if (cf === 3) b.u(1);
        b.ue(); b.ue(); b.u(1);
        if (b.u(1)) {
            for (var i = 0; i < (cf !== 3 ? 8 : 12); i++) {
                if (b.u(1)) {
                    var last = 8, next = 8;
                    for (var j = 0; j < (i < 6 ? 16 : 64); j++) {
                        if (next) next = (last + b.se() + 256) % 256;
                        last = next || last;
                    }
                }
            }
        }
    }
    b.ue();
    var poc = b.ue();
    if (poc === 0) b.ue();
    else if (poc === 1) { b.u(1); b.se(); b.se(); var nc = b.ue(); for (var k = 0; k < nc; k++) b.se(); }
    b.ue(); b.u(1);
    var w = (b.ue() + 1) * 16, hu = b.ue() + 1, fmo = b.u(1), h = hu * 16 * (2 - fmo);
    if (!fmo) b.u(1);
    b.u(1);
    if (b.u(1)) {
        var cl = b.ue(), cr = b.ue(), ct = b.ue(), cb = b.ue();
        var sx = cf === 1 || cf === 2 ? 2 : 1, sy = (cf === 1 ? 2 : 1) * (2 - fmo);
        w -= sx * (cl + cr); h -= sy * (ct + cb);
    }
    var fps = null;
    try {
        if (b.u(1)) {                                  // VUI
            if (b.u(1)) { if (b.u(8) === 255) { b.u(16); b.u(16); } }
            if (b.u(1)) b.u(1);
            if (b.u(1)) { b.u(3); b.u(1); if (b.u(1)) { b.u(8); b.u(8); b.u(8); } }
            if (b.u(1)) { b.ue(); b.ue(); }
            if (b.u(1)) {
                var units = b.u(32), scale = b.u(32);
                if (units && scale) {
                    var f = scale / (2 * units);
                    if (f >= 1 && f <= 240) fps = f;
                }
            }
        }
    } catch (_) {}
    return { width: w, height: h, fps: fps };
}

/** First SPS NAL in an Annex-B frame, or null. */
function findSps(f) {
    for (var i = 0; i + 4 < f.length && i < 4096; i++) {
        if (f[i] === 0 && f[i + 1] === 0 && f[i + 2] === 1 && (f[i + 3] & 0x1f) === 7) return f.subarray(i + 3);
    }
    return null;
}

/** MPEG-4 Part 2 VOL → { width, height, res, fixedInc|null }. */
function m4vVol(f) {
    for (var i = 0; i + 8 < f.length && i < 4096; i++) {
        if (f[i] !== 0 || f[i + 1] !== 0 || f[i + 2] !== 1 || f[i + 3] < 0x20 || f[i + 3] > 0x2f) continue;
        try {
            var b = new Bits(f.subarray(i + 4, i + 64), false);
            b.u(1); b.u(8);
            var verid = 1;
            if (b.u(1)) { verid = b.u(4); b.u(3); }
            if (b.u(4) === 15) { b.u(8); b.u(8); }
            if (b.u(1)) {
                b.u(2); b.u(1);
                if (b.u(1)) { b.u(15); b.u(1); b.u(15); b.u(1); b.u(15); b.u(1); b.u(3); b.u(11); b.u(1); b.u(15); b.u(1); }
            }
            var shape = b.u(2);
            if (shape === 3 && verid !== 1) b.u(4);
            if (shape !== 0) continue;
            b.u(1);
            var res = b.u(16);
            b.u(1);
            if (!res) continue;
            var nb = Math.max(1, Math.ceil(Math.log2(res)));
            var fixed = b.u(1), inc = null;
            if (fixed) inc = b.u(nb);
            b.u(1); var w = b.u(13); b.u(1); var h = b.u(13);
            if (!w || !h) continue;
            return { width: w, height: h, res: res, fixedInc: fixed && inc ? inc : null, nb: nb };
        } catch (_) {}
    }
    return null;
}

/** Display times of VOPs (decode order in) → the frame duration in ticks, or null. */
function vopStep(frames, vol) {
    var tbLast = 0, tbPrev = 0, times = [];
    for (var k = 0; k < frames.length; k++) {
        var f = frames[k];
        for (var i = 0; i + 8 < f.length && i < 512; i++) {
            if (f[i] !== 0 || f[i + 1] !== 0 || f[i + 2] !== 1 || f[i + 3] !== 0xb6) continue;
            try {
                var b = new Bits(f.subarray(i + 4, i + 16), false);
                var type = b.u(2), mtb = 0;
                while (b.u(1)) { if (++mtb > 60) throw new Error('mtb'); }
                b.u(1);
                var inc = b.u(vol.nb);
                var t;
                if (type === 2) t = (tbPrev + mtb) * vol.res + inc;           // B: past reference in display order
                else { tbPrev = tbLast; tbLast += mtb; t = tbLast * vol.res + inc; }
                times.push(t);
            } catch (_) {}
            break;
        }
    }
    times.sort(function (a, b) { return a - b; });
    var d = [];
    for (var j = 1; j < times.length; j++) if (times[j] > times[j - 1]) d.push(times[j] - times[j - 1]);
    if (d.length < 4) return null;
    d.sort(function (a, b) { return a - b; });
    return d[Math.floor(d.length / 4)];                // lower quartile: a dropped frame doubles a step, never halves it
}

var MPEG2_FPS = [0, 24000 / 1001, 24, 25, 30000 / 1001, 30, 50, 60000 / 1001, 60];

/** JPEG SOF → { width, height } (the same walk media-locate's jpegAt does). */
function jpegDims(f) {
    var p = 2, end = Math.min(f.length, 65536);
    for (var segs = 0; segs < 48 && p + 4 <= end; segs++) {
        if (f[p] !== 0xff) return null;
        var m = f[p + 1];
        while (m === 0xff && p + 2 < end) { p++; m = f[p + 1]; }
        if (m === 0xd8 || m === 0x01 || (m >= 0xd0 && m <= 0xd7)) { p += 2; continue; }
        var len = (f[p + 2] << 8) | f[p + 3];
        if (len < 2) return null;
        if (m === 0xda) return null;
        if (m >= 0xc0 && m <= 0xcf && m !== 0xc4 && m !== 0xc8 && m !== 0xcc) {
            var h = (f[p + 5] << 8) | f[p + 6], w = (f[p + 7] << 8) | f[p + 8];
            return w && h ? { width: w, height: h } : null;
        }
        p += 2 + len;
    }
    return null;
}

/* ── audio: what the chunks say about themselves ─────────────────────────── */

var MPA_BR = {
    1: [[0, 32, 64, 96, 128, 160, 192, 224, 256, 288, 320, 352, 384, 416, 448],
        [0, 32, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320, 384],
        [0, 32, 40, 48, 56, 64, 80, 96, 112, 128, 160, 192, 224, 256, 320]],
    2: [[0, 32, 48, 56, 64, 80, 96, 112, 128, 144, 160, 176, 192, 224, 256],
        [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160],
        [0, 8, 16, 24, 32, 40, 48, 56, 64, 80, 96, 112, 128, 144, 160]],
};
var MPA_SR = { 3: [44100, 48000, 32000], 2: [22050, 24000, 16000], 0: [11025, 12000, 8000] };

/** MPEG audio frame header at `i` → { layer, sr, ch, spf, size } | null. */
function mpaHeader(b, i) {
    if (b[i] !== 0xff || (b[i + 1] & 0xe0) !== 0xe0) return null;
    var ver = (b[i + 1] >> 3) & 3, lay = (b[i + 1] >> 1) & 3;
    if (ver === 1 || lay === 0) return null;
    var layer = 4 - lay, bri = b[i + 2] >> 4, sri = (b[i + 2] >> 2) & 3;
    if (bri === 0 || bri === 15 || sri === 3) return null;
    var br = MPA_BR[ver === 3 ? 1 : 2][layer - 1][bri] * 1000, sr = MPA_SR[ver][sri], pad = (b[i + 2] >> 1) & 1;
    var spf = layer === 1 ? 384 : (layer === 3 && ver !== 3 ? 576 : 1152);
    var size = layer === 1 ? (12 * br / sr + pad) * 4 : Math.floor(spf / 8 * br / sr) + pad;
    return { layer: layer, sr: sr, ch: (b[i + 3] >> 6) === 3 ? 1 : 2, spf: spf, size: Math.floor(size), br: br };
}

var AC3_SR = [48000, 44100, 32000];
var AC3_WORDS = [[64, 69, 96], [64, 70, 96], [80, 87, 120], [80, 88, 120], [96, 104, 144], [96, 105, 144], [112, 121, 168], [112, 122, 168],
    [128, 139, 192], [128, 140, 192], [160, 174, 240], [160, 175, 240], [192, 208, 288], [192, 209, 288], [224, 243, 336], [224, 244, 336],
    [256, 278, 384], [256, 279, 384], [320, 348, 480], [320, 349, 480], [384, 417, 576], [384, 418, 576], [448, 487, 672], [448, 488, 672],
    [512, 557, 768], [512, 558, 768], [640, 696, 960], [640, 697, 960], [768, 835, 1152], [768, 836, 1152], [896, 975, 1344], [896, 976, 1344],
    [1024, 1114, 1536], [1024, 1115, 1536], [1152, 1253, 1728], [1152, 1254, 1728], [1280, 1393, 1920], [1280, 1394, 1920]];
var AC3_CH = [2, 1, 2, 3, 3, 4, 4, 5];

function ac3Header(b, i) {
    if (b[i] !== 0x0b || b[i + 1] !== 0x77) return null;
    var fscod = b[i + 4] >> 6, frm = b[i + 4] & 0x3f, bsid = b[i + 5] >> 3;
    if (fscod > 2 || frm > 37 || bsid > 10) return null;
    var acmod = b[i + 6] >> 5, lfe = 0;
    /* lfeon sits after the mix levels that acmod switches on */
    var bits = 3, p = i + 6;
    if ((acmod & 1) && acmod !== 1) bits += 2;
    if (acmod & 4) bits += 2;
    if (acmod === 2) bits += 2;
    lfe = (b[p + (bits >> 3)] >> (7 - (bits & 7))) & 1;
    return { sr: AC3_SR[fscod], ch: AC3_CH[acmod] + lfe, size: AC3_WORDS[frm][fscod] * 2, spf: 1536 };
}

var ADTS_SR = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350];
function adtsHeader(b, i) {
    if (b[i] !== 0xff || (b[i + 1] & 0xf6) !== 0xf0) return null;
    var sri = (b[i + 2] >> 2) & 15, ch = ((b[i + 2] & 1) << 2) | (b[i + 3] >> 6);
    var len = ((b[i + 3] & 3) << 11) | (b[i + 4] << 3) | (b[i + 5] >> 5);
    if (sri > 12 || len < 7) return null;
    return { sr: ADTS_SR[sri], ch: ch || 2, size: len, spf: 1024, profile: (b[i + 2] >> 6) + 1, sri: sri, chCfg: ch };
}

/** Walk a frame chain from the first place 4 consecutive headers agree. */
function frameChain(b, parse) {
    /* Writers that pad silence with ZERO chunks (ffmpeg-audio-nok.avi: 38 KB of
       them in front of its AC-3) hid the first frames from a window that started
       at byte 0 — the sound came back "unknown" whenever the head survived. */
    var first = 0;
    while (first < b.length && b[first] === 0) first++;
    var lim = Math.min(b.length, first + 65536);
    for (var i = first; i + 8 < lim; i++) {
        var h = parse(b, i);
        if (!h || !h.size) continue;
        var p = i, n = 0, first = h;
        while (n < 4) {
            var hh = parse(b, p);
            if (!hh || hh.sr !== first.sr || !hh.size) break;
            n++; p += hh.size;
        }
        if (n < 4) continue;
        /* count the whole sample, resyncing across garbage */
        var frames = 0, bytes = 0;
        p = i;
        while (p + 8 < b.length) {
            var f = parse(b, p);
            if (f && f.sr === first.sr && f.size) { frames++; bytes += f.size; p += f.size; continue; }
            p++;
        }
        return { at: i, first: first, frames: frames, bytes: bytes };
    }
    return null;
}

/** IMA / MS ADPCM: every block starts with a header whose ranges are narrow. */
function adpcmLayout(chunks) {
    var sizes = chunks.map(function (c) { return c.length; });
    var tries = [];
    [256, 512, 1024, 2048, 4096, 128, 36, 72, 140, 1152].forEach(function (align) {
        [1, 2].forEach(function (ch) { tries.push({ align: align * (align < 256 && ch === 2 ? 2 : 1), ch: ch }); });
    });
    var full = sizes.slice(0, -1);
    function fits(align) { return full.length ? full.every(function (s) { return s % align === 0; }) : (sizes[0] % align === 0); }
    var best = null;
    tries.forEach(function (t) {
        if (!fits(t.align)) return;
        var ok = 0, tot = 0, okMs = 0;
        for (var c = 0; c < chunks.length && tot < 400; c++) {
            var b = chunks[c];
            for (var k = 0; k + t.align <= b.length && tot < 400; k += t.align) {
                tot++;
                var ima = true, ms = true;
                for (var j = 0; j < t.ch; j++) {
                    if (b[k + 4 * j + 2] > 88 || b[k + 4 * j + 3] !== 0) ima = false;
                    var delta = s16(b, k + t.ch + 2 * j);
                    if (b[k + j] > 6 || delta < 16) ms = false;
                }
                if (ima) ok++;
                if (ms) okMs++;
            }
        }
        if (tot < 6) return;
        /* Larger blocks first; at the same block size, stereo — a mono reading
           of a stereo block checks only the first header and passes too, and it
           doubles the sample rate (measured: 22 050 Hz stereo read as 44 100). */
        var better = function (c) { return !best || c.align > best.align || (c.align === best.align && c.ch > best.ch); };
        var ima = { codec: 'adpcm_ima_wav', tag: 0x11, align: t.align, ch: t.ch, spb: (t.align - 4 * t.ch) * 2 / t.ch + 1 };
        var ms = { codec: 'adpcm_ms', tag: 0x02, align: t.align, ch: t.ch, spb: (t.align - 7 * t.ch) * 2 / t.ch + 2 };
        if (ok === tot && better(ima)) best = ima;
        else if (okMs === tot && better(ms)) best = ms;
    });
    return best;
}

function gcd(a, b) { while (b) { var t = a % b; a = b; b = t; } return a; }

var PCM_KINDS = {
    u8:    { codec: 'pcm_u8', tag: 1, bits: 8, bps: 1, silence: 0x80 },
    s16le: { codec: 'pcm_s16le', tag: 1, bits: 16, bps: 2, silence: 0x00 },
    s24le: { codec: 'pcm_s24le', tag: 1, bits: 24, bps: 3, silence: 0x00 },
    f32le: { codec: 'pcm_f32le', tag: 3, bits: 32, bps: 4, silence: 0x00 },
    mulaw: { codec: 'pcm_mulaw', tag: 7, bits: 8, bps: 1, silence: 0xff },
    alaw:  { codec: 'pcm_alaw', tag: 6, bits: 8, bps: 1, silence: 0xd5 },
};

/**
 * What codec is in these audio payloads? `chunks` are the first payloads of the
 * stream (whole chunks, in order); `pcmGuess` is PcmLayoutGuess (injected: the
 * page loads it as a classic script, the tests load it through vm).
 */
async function sniffAudio(chunks, pcmGuess) {
    if (!chunks.length) return null;
    var all = concat(chunks);
    if (all.length < 64) return null;
    var c;
    /* VBR-style muxing puts exactly one frame in each chunk (AAC in AVI always,
       VBR MP3 usually): then a chunk IS a frame's duration, whatever its size.
       Otherwise the stream is CBR-packed and bytes convert at the frame rate. */
    function oneFrameEach(parse) {
        var n = 0, yes = 0;
        for (var q = 0; q < chunks.length && n < 200; q++) {
            if (chunks[q].length < 8) continue;
            n++;
            var hh = parse(chunks[q], 0);
            if (hh && Math.abs(hh.size - chunks[q].length) <= 1) yes++;
        }
        return n >= 4 && yes >= 0.9 * n;
    }
    if ((c = frameChain(all, mpaHeader))) {
        var h = c.first;
        return { codec: h.layer === 3 ? 'mp3' : (h.layer === 2 ? 'mp2' : 'mp1'), tag: h.layer === 3 ? 0x55 : 0x50,
                 sr: h.sr, ch: h.ch, spf: h.spf, frameBytes: c.bytes / Math.max(1, c.frames), exactRate: true, frameCoded: true,
                 oneFramePerChunk: oneFrameEach(mpaHeader) };
    }
    if ((c = frameChain(all, ac3Header))) {
        return { codec: 'ac3', tag: 0x2000, sr: c.first.sr, ch: c.first.ch, spf: 1536, frameBytes: c.first.size, exactRate: true, frameCoded: true,
                 oneFramePerChunk: oneFrameEach(ac3Header) };
    }
    if ((c = frameChain(all, adtsHeader))) {
        return { codec: 'aac', tag: 0xff, sr: c.first.sr, ch: c.first.ch, spf: 1024, frameBytes: c.bytes / Math.max(1, c.frames),
                 exactRate: true, frameCoded: true, adts: c.first, oneFramePerChunk: oneFrameEach(adtsHeader) };
    }
    var ad = adpcmLayout(chunks);
    if (ad) return Object.assign(ad, { bits: 4, exactRate: false, frameCoded: false });

    /* PCM. A block never straddles a chunk, so the block size divides every
       chunk but the last — that alone rules out most wrong readings. */
    var g = 0;
    for (var k = 0; k < chunks.length - 1; k++) g = gcd(g, chunks[k].length);
    if (!g) g = chunks[0].length;
    /* Digital silence says which format by the byte it is made of. */
    var first = all[0], flat = true;
    for (var q = 1; q < all.length && flat; q += 7) if (all[q] !== first) flat = false;
    if (flat) {
        var sk = first === 0x80 ? 'u8' : first === 0xff ? 'mulaw' : first === 0xd5 ? 'alaw' : 's16le';
        var sch = g % (PCM_KINDS[sk].bps * 2) === 0 && sk === 's16le' ? 1 : 1;
        return pcmResult(sk, sch, 0, true);
    }
    if (!pcmGuess || !pcmGuess.guess) return null;
    var fmts = ['u8', 's16le', 's24le', 'f32le', 'mulaw', 'alaw'], chans = [1, 2];
    fmts = fmts.filter(function (f) { return chans.some(function (ch) { return g % (PCM_KINDS[f].bps * ch) === 0; }); });
    if (!fmts.length) return null;
    var res = await pcmGuess.guess(new Blob([all]), { offset: 0, formats: fmts, channels: chans, knownAudio: true });
    var lim = pcmGuess.KNOWN_AUDIO_LIKE || 1.6;
    var cands = (res.candidates || []).filter(function (c) { return g % c.blockAlign === 0 && c.score < lim; });
    if (!cands.length || res.verdict !== 'audio') return null;
    var best = cands[0];
    var out = pcmResult(best.fmt, best.channels, best.score, false, cands.slice(1, 3).map(function (c) { return c.fmt + '/' + c.channels; }));
    if (best.lawScores) out.lawScores = best.lawScores;
    return out;
}
function pcmResult(fmt, ch, score, silent, alts) {
    var k = PCM_KINDS[fmt];
    return { codec: k.codec, fmt: fmt, tag: k.tag, bits: k.bits, ch: ch, align: k.bps * ch, spb: 1,
             exactRate: false, frameCoded: false, pcm: true, silent: !!silent, score: score, alts: alts || [] };
}

/** Samples in one audio chunk of `size` bytes for a sniffed audio description. */
function samplesIn(a, size, payload) {
    if (a.pcm) return Math.floor(size / a.align);
    if (a.codec === 'adpcm_ima_wav' || a.codec === 'adpcm_ms') return Math.floor(size / a.align) * a.spb;
    if (a.oneFramePerChunk) return size > 0 ? a.spf : 0;
    return size / a.frameBytes * a.spf;           // CBR-packed: exact over the stream, approximate per chunk
}

/* ── rates ───────────────────────────────────────────────────────────────── */

var SR_PRIOR = { 8000: 0.30, 11025: 0.25, 12000: 0.02, 16000: 0.15, 22050: 0.25, 24000: 0.05, 32000: 0.12, 44100: 0.25, 48000: 0.30 };
var FPS_PRIOR = [[30, 1.0], [25, 0.9], [30000 / 1001, 0.8], [15, 0.6], [24, 0.5], [20, 0.4], [24000 / 1001, 0.3], [60, 0.3],
                 [50, 0.2], [60000 / 1001, 0.15], [12, 0.2], [10, 0.2], [12.5, 0.15]];

function fpsPrior(fps, tol) {
    for (var i = 0; i < FPS_PRIOR.length; i++) if (Math.abs(fps - FPS_PRIOR[i][0]) / FPS_PRIOR[i][0] <= tol) return FPS_PRIOR[i][1];
    if (fps >= 5 && fps <= 35) return 0.12;       // cameras: 11.73 (Casio), 13.24 — real, and not on any list
    if (fps > 35 && fps <= 65) return 0.03;
    return 0;
}
function snapFps(fps, tol) {
    for (var i = 0; i < FPS_PRIOR.length; i++) if (Math.abs(fps - FPS_PRIOR[i][0]) / FPS_PRIOR[i][0] <= tol) return FPS_PRIOR[i][0];
    return fps;
}
function snapSr(sr, tol) {
    var list = Object.keys(SR_PRIOR).map(Number).concat([11024, 96000]);
    var best = null;
    list.forEach(function (s) { var e = Math.abs(sr - s) / s; if (e <= tol && (!best || e < best.e)) best = { s: s, e: e }; });
    return best ? best.s : Math.round(sr);
}

/* The sample rate a one-second chunk holds, or null. The CLOSEST listed rate: Canon
   writes 11 024 Hz, which is within 0.05 % of 11 025 too, and taking the first match
   put a one-sample-per-second drift into every Canon clip (54 ms over ten minutes). */
function oneSecondRate(samples) {
    var list = Object.keys(SR_PRIOR).map(Number).concat([11024]), best = null, bestErr = 0.0005;
    for (var i = 0; i < list.length; i++) {
        var e = Math.abs(samples - list[i]) / list[i];
        if (e <= bestErr) { best = list[i]; bestErr = e; }
    }
    return best;
}

/**
 * Pick frame rate and sample rate. Pure: everything it needs is in `x`.
 *   x.videoFps      exact fps the video bitstream carries (H.264 VUI, VOL, DV) or null
 *   x.audio         sniffed audio (exactRate + sr for frame-coded codecs) or null
 *   x.R             audio samples per video frame (interleave slope) or 0
 *   x.Rtol          relative precision of R
 *   x.oneSec        samples in the dominant audio chunk (one-second writers) or 0
 */
function solveRates(x) {
    var a = x.audio, tol = Math.min(0.01, Math.max(0.004, x.Rtol || 0));
    if (x.videoFps) {
        var fps = x.videoFps, sr = null, src = 'video';
        if (a && a.exactRate) sr = a.sr;
        else if (a && x.R) sr = snapSr(x.R * fps, Math.max(0.02, 2 * tol));
        return { fps: fps, sr: sr, source: src, ambiguous: false, alternatives: [] };
    }
    if (a && a.exactRate && x.R) {
        var f = a.sr / x.R;
        return { fps: snapFps(f, tol), sr: a.sr, source: 'audio', ambiguous: false, alternatives: [] };
    }
    if (a && x.R) {
        if (x.oneSec) {
            var list = Object.keys(SR_PRIOR).map(Number).concat([11024]);
            for (var m = 1; m <= 2; m++) {
                var hit = oneSecondRate(x.oneSec * m);
                if (hit) {
                    var f1 = hit / x.R;
                    if (f1 >= 3 && f1 <= 120) return { fps: snapFps(f1, tol), sr: hit, source: 'one-second-chunks', ambiguous: false, alternatives: [] };
                }
            }
        }
        var cands = Object.keys(SR_PRIOR).map(Number).map(function (s) {
            var fp = s / x.R;
            return { sr: s, fps: snapFps(fp, tol), score: SR_PRIOR[s] * fpsPrior(fp, tol) };
        }).filter(function (c) { return c.score > 0; }).sort(function (p, q) { return q.score - p.score; });
        if (!cands.length) return { fps: 30, sr: null, source: 'default', ambiguous: true, alternatives: [] };
        var top = cands[0], second = cands[1];
        return { fps: top.fps, sr: top.sr, source: 'prior', ambiguous: !!(second && second.score > top.score / 2),
                 alternatives: cands.slice(0, 4).map(function (c) { return { fps: c.fps, sr: c.sr }; }) };
    }
    /* No audio to lean on and no timing in the bitstream (MJPEG): the rate is
       simply not in the file. A one-second writer still names its sample rate. */
    var srKnown = a ? (a.sr || null) : null;
    if (!srKnown && a && x.oneSec) srKnown = oneSecondRate(x.oneSec);
    var dflt = [30, 25, 15, 24, 20, 10];
    return { fps: 30, sr: srKnown, source: 'default', ambiguous: true,
             alternatives: dflt.map(function (f) { return { fps: f, sr: srKnown }; }) };
}

/* ── walk ────────────────────────────────────────────────────────────────── */

/**
 * Follow the chunk chain from `at` to the end of the file. Reads the file once,
 * sequentially, in 8 MB windows; keeps the table (≈16 bytes a chunk) and the
 * first frames of each stream for sniffing.
 */
async function walk(file, opts) {
    var o = opts || {};
    var t = new Table();
    var pos = o.at || 0, seg = 0, buf = null, bufAt = 0, sids = null;
    var holes = [], slops = 0, idx1At = -1, idx1 = null, endReason = 'eof', family = null;
    var pendingHeads = [], firstVideo = {}, firstAudio = {}, videoKeep = o.videoKeep || 24, audioKeepBytes = o.audioKeepBytes || 2 * MB;
    var audioKept = {};

    async function ensure(p, need) {
        if (buf && p >= bufAt && p + need <= bufAt + buf.length) return true;
        if (p >= file.size) return false;
        buf = await readAt(file, p, Math.max(WINDOW, need)); bufAt = p;
        return p + need <= bufAt + buf.length;
    }
    async function bytesAt(p, len) {
        if (buf && p >= bufAt && p + len <= bufAt + buf.length) return buf.subarray(p - bufAt, p - bufAt + len);
        return readAt(file, p, len);
    }
    function decideFamily() {
        var votes = {};
        pendingHeads.forEach(function (ph) { var f = familyOf(ph.head); if (f) votes[f] = (votes[f] || 0) + 1; });
        var best = null;
        Object.keys(votes).forEach(function (f) { if (!best || votes[f] > votes[best]) best = f; });
        family = best || 'unknown';
        pendingHeads.forEach(function (ph) { t.key[ph.i] = family === 'unknown' ? 2 : isKey(family, ph.head); });
        pendingHeads = [];
    }

    /* Where does the next link sit, when the one here is not a chunk? */
    async function resync(p) {
        var lo = Math.max(o.at || 0, p - SLOP), win = await readAt(file, lo, 2 * SLOP + 4096);
        for (var d = 0; d <= SLOP; d++) {
            for (var sgn = -1; sgn <= 1; sgn += 2) {
                var q = p + sgn * d;
                if (q < lo || (d === 0 && sgn > 0)) continue;
                var j = q - lo;
                if (j + 12 <= win.length && chainLen(win, j, win.length, 2, sids) >= 2) { slops++; return q; }
            }
        }
        /* A hole: zeros, garbage, somebody else's cluster. Scan forward for the
           next place three links hold, among the streams we already have. */
        var at = p;
        while (at < file.size) {
            if (o.signal && o.signal.aborted) return -1;
            var w = await readAt(file, at, WINDOW + 4096), n = w.length;
            for (var i = 0; i + 12 <= n; i++) {
                if (!isDigit(w[i]) && w[i] !== 0x4c && w[i] !== 0x52) continue;
                if (chainLen(w, i, n, 3, sids) >= 3) return at + i;
            }
            if (n < WINDOW + 4096) break;
            at += WINDOW;
            if (o.onProgress) o.onProgress(Math.min(1, at / file.size));
        }
        return -1;
    }

    var lastProgress = 0;
    while (pos + 8 <= file.size) {
        if (o.signal && o.signal.aborted) return null;
        if (!(await ensure(pos, 12))) break;
        var i = pos - bufAt;
        var k = streamKind(buf, i);
        if (k) {
            var sz = u32(buf, i + 4), sid = sidOf(buf, i);
            /* In the chain every stream counts — a Canon writes twenty frames
               before its first audio chunk. Only a RESYNC (after a hole) is held
               to the streams already seen. */
            if (sz <= MAX_CHUNK) {
                if (pos + 8 + sz > file.size) { endReason = 'truncated'; break; }
                var key = k === K_VIDEO || k === K_DV ? 2 : 1;
                var idx = t.push(pos, sz, sid, k, key, seg);
                if (k === K_VIDEO || k === K_DV) {
                    var head = await bytesAt(pos + 8, Math.min(sz, HEAD));
                    if (family) t.key[idx] = family === 'unknown' ? 2 : isKey(family, head);
                    else { pendingHeads.push({ i: idx, head: head.slice() }); if (pendingHeads.length >= 8) decideFamily(); }
                    var fv = firstVideo[sid] || (firstVideo[sid] = []);
                    if (fv.length < videoKeep && sz > 0) fv.push(idx);
                } else if (k === K_AUDIO) {
                    var ak = audioKept[sid] || 0;
                    if (ak < audioKeepBytes && sz > 0) { (firstAudio[sid] || (firstAudio[sid] = [])).push(idx); audioKept[sid] = ak + sz; }
                }
                (sids || (sids = {}))[sid] = true;
                pos += 8 + sz + (sz & 1);
                if (o.onProgress && pos - lastProgress > 32 * MB) { lastProgress = pos; o.onProgress(Math.min(1, pos / file.size)); }
                continue;
            }
        }
        var st = structKind(buf, i);
        if (st) {
            var ssz = u32(buf, i + 4);
            if (st === S_LIST || st === S_RIFF) {
                var type = fcc(buf, i + 8);
                if (type === 'movi' || type === 'rec ' || type === 'AVIX') { pos += 12; continue; }
                if (type === 'AVI ' || type === 'hdrl') { endReason = 'next-file'; break; }
            }
            if (st === S_IDX1) {
                idx1At = pos;
                if (pos + 8 + ssz <= file.size && ssz <= 256 * MB) idx1 = parseIdx1(await readAt(file, pos + 8, ssz));
                if (pos + 8 + ssz <= file.size) {
                    /* After idx1 only an OpenDML continuation belongs to this file;
                       anything else is the next thing on the disk. */
                    var nx = pos + 8 + ssz + (ssz & 1);
                    var peek = await readAt(file, nx, 12);
                    if (peek.length >= 12 && structKind(peek, 0) === S_RIFF && fcc(peek, 8) === 'AVIX') { pos = nx; continue; }
                    endReason = 'idx1'; break;
                }
                endReason = 'idx1-truncated'; break;
            }
            if (ssz <= MAX_CHUNK) { pos += 8 + ssz + (ssz & 1); continue; }
        }
        var q = await resync(pos);
        if (q < 0) { endReason = 'hole-to-eof'; holes.push({ at: pos, len: file.size - pos }); break; }
        if (Math.abs(q - pos) > SLOP) holes.push({ at: pos, len: q - pos });
        seg++;
        pos = q;
    }
    if (pendingHeads.length) decideFamily();
    return { table: t, at: o.at || 0, holes: holes, slops: slops, idx1At: idx1At, idx1: idx1, endReason: endReason,
             family: family, firstVideo: firstVideo, firstAudio: firstAudio,
             end: t.n ? t.off[t.n - 1] + 8 + t.size[t.n - 1] + (t.size[t.n - 1] & 1) : pos };
}

/**
 * Where the stream chunks of a RIFF AVI begin when its header cannot be
 * trusted (MediaLocate says `container/avi`, FFmpeg finds no video in it): the
 * `movi` list if it is still there and its first links hold, else the first
 * place three links chain. -1 when neither — then this is not a header problem.
 *
 * Measured on the corpus with `hdrl` zeroed inside an intact RIFF (2026-10-10):
 * FFmpeg reads 0 frames from every one of the 25, the walk from here reads
 * them all.
 */
async function bodyStart(file, opts) {
    var o = opts || {}, from = o.at || 0;
    var head = await readAt(file, from, 16 * MB);
    for (var i = 8; i + 12 <= head.length; i++) {
        if (head[i] !== 0x6d || head[i + 1] !== 0x6f || head[i + 2] !== 0x76 || head[i + 3] !== 0x69) continue;   // 'movi'
        if (fcc(head, i - 8) !== 'LIST') continue;
        if (chainLen(head, i + 4, head.length, 2) >= 1) return from + i + 4;
    }
    /* No `movi` (or a torn one): the first chain, within the reach of a header.
       A header is kilobytes; a chain that starts 64 MB in is a different case. */
    for (var at = from; at < file.size && at - from < 64 * MB; at += WINDOW) {
        var w = await readAt(file, at, WINDOW + 4096), n = w.length;
        for (var j = 0; j + 12 <= n; j++) {
            if (isDigit(w[j]) && chainLen(w, j, n, 3) >= 3) return at + j;
        }
        if (n < WINDOW + 4096) break;
    }
    return -1;
}

/** idx1 -> typed arrays: stream id, kind, flags, offset, size — in file order. */
function parseIdx1(b) {
    var n = Math.floor(b.length / 16), sid = new Uint8Array(n), kind = new Uint8Array(n);
    var off = new Float64Array(n), size = new Uint32Array(n), flags = new Uint32Array(n), m = 0;
    for (var i = 0; i < n; i++) {
        var k = streamKind(b, i * 16);
        if (!k) continue;
        sid[m] = sidOf(b, i * 16); kind[m] = k;
        flags[m] = u32(b, i * 16 + 4); off[m] = u32(b, i * 16 + 8); size[m] = u32(b, i * 16 + 12);
        m++;
    }
    return { n: m, sid: sid, kind: kind, flags: flags, off: off, size: size };
}

/**
 * The original index survived (the tail is intact): find where our first
 * chunks sit in it. That says exactly how many frames and samples the lost
 * head held, and what the whole recording's totals were — the two things the
 * interleave can only estimate. Matching is on sid + size + the offset
 * DIFFERENCES of 24 consecutive chunks, so absolute/relative idx1 offsets and a
 * shifted file (head cut off) match alike.
 */
function idx1Anchor(w, vsid, asid, audio) {
    var ix = w.idx1, t = w.table;
    if (!ix || !ix.n || !t.n) return null;
    var ours = [];
    for (var i = 0; i < t.n && ours.length < 24; i++) if (t.sid[i] === vsid || t.sid[i] === asid) ours.push(i);
    if (ours.length < 4) return null;
    var e0 = -1;
    for (var e = 0; e + ours.length <= ix.n && e0 < 0; e++) {
        if (ix.sid[e] !== t.sid[ours[0]] || ix.size[e] !== t.size[ours[0]]) continue;
        var ok = true, q = e;
        for (var j = 1; j < ours.length && ok; j++) {
            q++;
            while (q < ix.n && ix.sid[q] !== vsid && ix.sid[q] !== asid) q++;
            if (q >= ix.n || ix.sid[q] !== t.sid[ours[j]] || ix.size[q] !== t.size[ours[j]]
                || ix.off[q] - ix.off[e] !== t.off[ours[j]] - t.off[ours[0]]) ok = false;
        }
        if (ok) e0 = e;
    }
    if (e0 < 0) return null;
    var framesBefore = 0, samplesBefore = 0, totalFrames = 0, totalSamples = 0;
    for (var k = 0; k < ix.n; k++) {
        if (ix.sid[k] === vsid) { totalFrames++; if (k < e0) framesBefore++; }
        else if (ix.sid[k] === asid && audio) { var sm = samplesIn(audio, ix.size[k]); totalSamples += sm; if (k < e0) samplesBefore += sm; }
    }
    return { e0: e0, framesBefore: framesBefore, samplesBefore: samplesBefore, totalFrames: totalFrames, totalSamples: totalSamples };
}

/* ── examine: from the table to a description ────────────────────────────── */

async function payloads(file, t, idxs, cap) {
    var out = [];
    for (var j = 0; j < idxs.length; j++) {
        var i = idxs[j];
        out.push(await readAt(file, t.off[i] + 8, Math.min(t.size[i], cap || MAX_CHUNK)));
    }
    return out;
}

/**
 * The description a header needs, learned from the walk. Throws nothing:
 * whatever cannot be learned is null, and `rebuild` decides what that means.
 */
async function examine(file, opts) {
    var o = opts || {};
    var w = o.walked || await walk(file, o);
    if (!w || !w.table.n) return null;
    var t = w.table;

    /* Streams in this file: their sids, kinds and counts. */
    var streams = {};
    for (var i = 0; i < t.n; i++) {
        var s = streams[t.sid[i]] || (streams[t.sid[i]] = { sid: t.sid[i], kind: t.kind[i], count: 0, bytes: 0, maxSize: 0 });
        if (t.kind[i] === K_VIDEO || t.kind[i] === K_DV) s.kind = t.kind[i];
        s.count++; s.bytes += t.size[i]; if (t.size[i] > s.maxSize) s.maxSize = t.size[i];
    }
    var vids = Object.keys(streams).map(Number).filter(function (s) { return streams[s].kind === K_VIDEO || streams[s].kind === K_DV; });
    if (!vids.length) return null;
    vids.sort(function (p, q) { return streams[q].count - streams[p].count; });
    var vsid = vids[0], auds = Object.keys(streams).map(Number).filter(function (s) { return streams[s].kind === K_AUDIO; });
    auds.sort(function (p, q) { return streams[q].bytes - streams[p].bytes; });
    var asid = auds.length ? auds[0] : -1;

    /* Video: family from the walk, parameters from whole frames. */
    var vframes = await payloads(file, t, w.firstVideo[vsid] || [], 2 * MB);
    var video = { sid: vsid, family: w.family, kind: streams[vsid].kind, frames: 0, width: 0, height: 0, fps: null, fourcc: null, fpsSource: null };
    var keyIdx = -1;
    for (var n = 0; n < t.n; n++) {
        if (t.sid[n] !== vsid) continue;
        video.frames++;
        if (keyIdx < 0 && t.size[n] > 0 && t.key[n] === 1) keyIdx = n;
    }
    /* H.264 decodes from the first keyframe that brings its SPS AND PPS — an
       IDR whose PPS went with the lost head decodes to "non-existing PPS 0"
       (measured: 366 errors on a DVB capture before this rule). */
    if (keyIdx >= 0 && w.family === 'h264') {
        var tried = 0, found = -1;
        for (var kk = keyIdx; kk < t.n && tried < 96 && found < 0; kk++) {
            if (t.sid[kk] !== vsid || t.key[kk] !== 1) continue;
            tried++;
            var hd = await readAt(file, t.off[kk] + 8, Math.min(t.size[kk], 8192)), sps = false, pps = false;
            for (var z0 = 0; z0 + 3 < hd.length; z0++) {
                if (hd[z0] === 0 && hd[z0 + 1] === 0 && hd[z0 + 2] === 1) { var nt = hd[z0 + 3] & 0x1f; if (nt === 7) sps = true; if (nt === 8) pps = true; }
            }
            if (sps && pps) found = kk;
        }
        if (found >= 0) keyIdx = found;
        /* No keyframe in reach brings both: the parameter sets were sent once,
           at the start of the recording, and went with the head. Nothing in
           these frames can replace them — a sample clip from the same camera
           can (the donor lane). */
        else video.paramsMissing = true;
    }
    var keyFrame = keyIdx >= 0 ? (await payloads(file, t, [keyIdx], 2 * MB))[0] : null;
    var fam = w.family;
    if (fam === 'mjpeg') {
        var dims = null;
        for (var v = 0; v < vframes.length && !dims; v++) dims = jpegDims(vframes[v]);
        if (dims) Object.assign(video, { codec: 'mjpeg', fourcc: 'MJPG', width: dims.width, height: dims.height });
    } else if (fam === 'dv') {
        var f0 = vframes[0] || new Uint8Array(0), pal = f0.length > 3 && (f0[3] & 0x80) !== 0;
        var mult = Math.round((f0.length || streams[vsid].maxSize) / (pal ? 144000 : 120000));
        Object.assign(video, { codec: 'dvvideo', fourcc: mult >= 2 ? 'dv50' : 'dvsd', width: 720, height: pal ? 576 : 480,
                               fps: pal ? 25 : 30000 / 1001, fpsSource: 'dv' });
    } else if (fam === 'h264' && keyFrame) {
        var sps = findSps(keyFrame);
        if (sps) {
            try {
                var p = h264Sps(sps);
                Object.assign(video, { codec: 'h264', fourcc: 'H264', width: p.width, height: p.height, fps: p.fps, fpsSource: p.fps ? 'sps' : null });
            } catch (_) {}
        }
    } else if (fam === 'mpeg4') {
        var vol = null, volAt = -1;
        for (var m = 0; m < vframes.length && !vol; m++) { vol = m4vVol(vframes[m]); volAt = m; }
        if (!vol && keyFrame) vol = m4vVol(keyFrame);
        if (vol) {
            var fpsM = vol.fixedInc ? vol.res / vol.fixedInc : null, srcM = fpsM ? 'vol' : null;
            if (!fpsM) { var step = vopStep(vframes, vol); if (step) { fpsM = vol.res / step; srcM = 'vop'; } }
            if (fpsM && (fpsM < 1 || fpsM > 240)) fpsM = null;
            Object.assign(video, { codec: 'mpeg4', fourcc: 'XVID', width: vol.width, height: vol.height, fps: fpsM, fpsSource: fpsM ? srcM : null, vol: true });
        }
    } else if (fam === 'mpeg2' && keyFrame) {
        for (var q = 0; q + 12 < keyFrame.length && q < 4096; q++) {
            if (keyFrame[q] === 0 && keyFrame[q + 1] === 0 && keyFrame[q + 2] === 1 && keyFrame[q + 3] === 0xb3) {
                var wq = (keyFrame[q + 4] << 4) | (keyFrame[q + 5] >> 4), hq = ((keyFrame[q + 5] & 15) << 8) | keyFrame[q + 6];
                var frc = keyFrame[q + 7] & 15;
                Object.assign(video, { codec: 'mpeg2video', fourcc: 'mpg2', width: wq, height: hq, fps: MPEG2_FPS[frc] || null, fpsSource: MPEG2_FPS[frc] ? 'seq' : null });
                break;
            }
        }
    }

    /* Audio: codec from payloads; then samples per chunk for the whole table. */
    var audio = null;
    if (asid >= 0 && o.audio) {
        /* Described by the donor (raw AAC and the like name nothing themselves):
           the interleave below still needs it, or the sound is never placed. */
        audio = Object.assign({}, o.audio, { sid: asid });
    } else if (asid >= 0) {
        var ach = await payloads(file, t, w.firstAudio[asid] || [], 4 * MB);
        audio = await sniffAudio(ach, o.pcmGuess || (typeof window !== 'undefined' ? window.PcmLayoutGuess : null));
        if (audio) audio.sid = asid;
    }

    /* Interleave: cumulative audio samples at each audio chunk vs. video frames
       written before it. Its slope is samples per frame (exact, whatever the
       rates are); its intercept is where the audio sits against the video. */
    var pts = [], vBefore = 0, aBefore = 0, sizes = {};
    for (var z = 0; z < t.n; z++) {
        if (t.sid[z] === vsid) { vBefore++; continue; }
        if (!audio || t.sid[z] !== asid) continue;
        var sm = samplesIn(audio, t.size[z]);
        pts.push({ v: vBefore, a: aBefore, s: sm, i: z });
        aBefore += sm;
        sizes[t.size[z]] = (sizes[t.size[z]] || 0) + 1;
    }
    var R = 0, Rtol = 0, interleaved = false, oneSec = 0;
    if (audio && pts.length) {
        var vs = pts.map(function (p) { return p.v; }), span = vs[vs.length - 1] - vs[0];
        interleaved = pts.length >= 3 && span >= 0.5 * video.frames;
        if (interleaved) {
            var mv = 0, ma = 0;
            pts.forEach(function (p) { mv += p.v; ma += p.a; });
            mv /= pts.length; ma /= pts.length;
            var sxy = 0, sxx = 0;
            pts.forEach(function (p) { sxy += (p.v - mv) * (p.a - ma); sxx += (p.v - mv) * (p.v - mv); });
            R = sxx ? sxy / sxx : 0;
            /* Precision from how well the chunks sit on that line, over the span
               the line covers: exact writers (every camera measured) give ~0. */
            var ssr = 0;
            pts.forEach(function (p) { var e = p.a - (ma + R * (p.v - mv)); ssr += e * e; });
            var se = pts.length > 2 ? Math.sqrt(ssr / (pts.length - 2)) : pts[0].s;
            Rtol = R ? Math.min(0.2, 2 * se / Math.max(1, R * span)) : 0.2;
        } else if (video.frames) {
            R = aBefore / video.frames;                 // a block layout: only the totals relate them
            Rtol = 0.05;
        }
        /* One-second writers: the chunk that dominates holds a round number of
           samples that IS a sample rate. Needs a few of them to count. */
        var domKey = Object.keys(sizes).sort(function (p, q) { return sizes[q] - sizes[p]; })[0];
        if (domKey && sizes[domKey] >= 3 && audio.pcm !== undefined && !audio.frameCoded) oneSec = samplesIn(audio, +domKey);
    }

    /* The surviving index, when there is one, outranks the interleave: the
       whole recording's totals relate the rates exactly, block layouts included
       (a Casio stores all its audio after the video — no interleave to read). */
    var anchor = idx1Anchor(w, vsid, asid, audio);
    var blockLayout = !!(audio && pts.length && !interleaved);
    if (anchor && anchor.totalFrames && anchor.totalSamples) {
        R = anchor.totalSamples / anchor.totalFrames;
        Rtol = 1 / anchor.totalFrames;
    } else if (blockLayout) {
        /* All the audio in one block (a Casio writes it after the video) and no
           index: the totals relate what SURVIVED of each stream, not the rates.
           Nothing here knows the frame rate — say so rather than invent it. */
        R = 0;
    }
    var rates = solveRates({ videoFps: video.fps, audio: audio, R: R, Rtol: Rtol, oneSec: oneSec });

    return { walked: w, video: video, audio: audio, streams: streams, keyIdx: keyIdx, anchor: anchor, blockLayout: blockLayout,
             interleave: { R: R, Rtol: Rtol, points: pts, interleaved: interleaved, oneSec: oneSec },
             rates: rates };
}

/* ── sync ────────────────────────────────────────────────────────────────── */

/**
 * Seconds of the region's audio (from its first surviving sample) at which the
 * key frame plays. Negative = the audio starts that much AFTER the key frame.
 * Rule by what survived: tail intact (idx1) → both streams end together;
 * otherwise → interleave regression with the writer's offset taken as zero.
 */
function audioStartFor(x, fps, sr, keyIdx) {
    var w = x.walked, t = w.table, pts = x.interleave.points;
    if (!pts.length || !fps || !sr) return { want: 0, rule: 'none' };
    var v0 = 0, keyFrame = 0;
    for (var i = 0; i < t.n; i++) { if (t.sid[i] !== x.video.sid) continue; if (i === keyIdx) break; keyFrame++; }
    if (x.anchor) {
        /* Exact: the key frame's time in the original recording, minus the
           time of the first audio sample we still have. */
        return { want: (x.anchor.framesBefore + keyFrame) / fps - x.anchor.samplesBefore / sr, rule: 'idx1' };
    }
    var framesAll = x.video.frames, samplesAll = pts[pts.length - 1].a + pts[pts.length - 1].s;
    var tail = w.endReason === 'idx1';
    if (tail) return { want: samplesAll / sr - framesAll / fps + keyFrame / fps, rule: 'tail' };
    if (!x.interleave.interleaved) return { want: 0, rule: 'unknown' };
    var ks = pts.map(function (p) { return p.a / sr - (p.v - v0) / fps; }).sort(function (p, q) { return p - q; });
    var K = ks[Math.floor(ks.length / 2)];
    return { want: K + keyFrame / fps, rule: 'interleave' };
}

/* ── header ──────────────────────────────────────────────────────────────── */

function ck(tag, payload) {
    var pad = payload.length & 1, out = new Uint8Array(8 + payload.length + pad);
    for (var i = 0; i < 4; i++) out[i] = tag.charCodeAt(i);
    new DataView(out.buffer).setUint32(4, payload.length, true);
    out.set(payload, 8);
    return out;
}
function list(type, parts) { return ck('LIST', concat([strBytes(type)].concat(parts))); }
function strBytes(s) { var b = new Uint8Array(s.length); for (var i = 0; i < s.length; i++) b[i] = s.charCodeAt(i) & 0xff; return b; }
function le(fields) {
    /* fields: [[bytes, value], …] little-endian; bytes 2 or 4, or a 4-char string */
    var n = 0; fields.forEach(function (f) { n += f[0]; });
    var out = new Uint8Array(n), dv = new DataView(out.buffer), o = 0;
    fields.forEach(function (f) {
        if (typeof f[1] === 'string') out.set(strBytes(f[1]), o);
        else if (f[0] === 2) dv.setInt16(o, f[1], true);
        else dv.setUint32(o, f[1] >>> 0, true);
        o += f[0];
    });
    return out;
}

function fpsFraction(fps) {
    var known = [[24000, 1001], [30000, 1001], [60000, 1001]];
    for (var i = 0; i < known.length; i++) if (Math.abs(fps - known[i][0] / known[i][1]) < 0.002) return { scale: known[i][1], rate: known[i][0] };
    if (Math.abs(fps - Math.round(fps)) < 1e-6) return { scale: 1, rate: Math.round(fps) };
    return { scale: 1000000, rate: Math.round(fps * 1000000) };
}

var MS_ADPCM_COEF = [256, 0, 512, -256, 0, 0, 192, 64, 240, 0, 460, -208, 392, -232];

function waveFormat(a, sr) {
    var ch = a.ch, extra = new Uint8Array(0), tag = a.tag, align, byteRate, bits;
    if (a.pcm) { align = a.align; byteRate = sr * align; bits = a.bits; }
    else if (a.codec === 'adpcm_ima_wav') {
        align = a.align; bits = 4; byteRate = Math.round(sr * align / a.spb);
        extra = le([[2, a.spb]]);
    } else if (a.codec === 'adpcm_ms') {
        align = a.align; bits = 4; byteRate = Math.round(sr * align / a.spb);
        extra = le([[2, a.spb], [2, 7]].concat(MS_ADPCM_COEF.map(function (c) { return [2, c]; })));
    } else if (a.codec === 'mp3') {
        align = 1; bits = 0; byteRate = Math.round(a.frameBytes * sr / a.spf);
        extra = le([[2, 1], [4, 2], [2, Math.round(a.frameBytes)], [2, 1], [2, 1393]]);
    } else if (a.codec === 'mp2' || a.codec === 'mp1') {
        align = 1; bits = 0; byteRate = Math.round(a.frameBytes * sr / a.spf);
    } else if (a.codec === 'ac3') {
        align = 1; bits = 0; byteRate = Math.round(a.frameBytes * sr / a.spf);
    } else if (a.codec === 'aac') {
        align = 1; bits = 0; byteRate = Math.round(a.frameBytes * sr / a.spf);
    }
    var head = le([[2, tag], [2, ch], [4, sr], [4, byteRate], [2, align], [2, bits], [2, extra.length]]);
    return { bytes: concat([head, extra]), align: align, byteRate: byteRate };
}

/** strh timing for the audio stream, by FFmpeg's own convention per codec. */
function audioTiming(a, sr, wf) {
    if (a.pcm) return { scale: a.align, rate: sr * a.align, sampleSize: a.align };
    if (a.codec === 'adpcm_ima_wav' || a.codec === 'adpcm_ms') return { scale: a.spb, rate: sr, sampleSize: a.align };
    /* One frame per chunk (AAC in AVI always, VBR MP3 usually): the VBR convention
       FFmpeg itself writes — each chunk IS one frame of `spf` samples (dwScale = spf,
       dwRate = sample rate, dwSampleSize = 0). Byte timing here made VBR timestamps
       jitter, and a `-c copy` of the rebuilt file collided its own audio DTS: 1 076
       "non monotonically increasing dts" lines on a H.264 + AAC film (2026-10-10). */
    if (a.frameCoded && a.oneFramePerChunk && a.spf) return { scale: a.spf, rate: sr, sampleSize: 0 };
    /* CBR-packed frames: timing from the bytes, and FFmpeg's audio parser takes the
       frames' own durations over from there. */
    return { scale: 1, rate: wf.byteRate, sampleSize: 1 };
}

function buildHeader(spec) {
    var v = spec.video, streamsHdr = [], fr = fpsFraction(spec.fps);
    var order = spec.order;                              // [{sid, type:'video'|'audio'|'dvi'}] by sid
    order.forEach(function (s) {
        if (s.type === 'video' || s.type === 'dvi') {
            var strh = concat([strBytes(s.type === 'dvi' ? 'iavs' : 'vids'), strBytes(v.fourcc),
                le([[4, 0], [2, 0], [2, 0], [4, 0], [4, fr.scale], [4, fr.rate], [4, spec.videoStart || 0], [4, spec.frames],
                    [4, spec.maxVideo], [4, 0xffffffff], [4, 0], [2, 0], [2, 0], [2, v.width], [2, v.height]])]);
            var strf = s.type === 'dvi' ? new Uint8Array(32)
                : le([[4, 40], [4, v.width], [4, v.height], [2, 1], [2, 24], [4, v.fourcc], [4, v.width * v.height * 3], [4, 0], [4, 0], [4, 0], [4, 0]]);
            streamsHdr.push(list('strl', [ck('strh', strh), ck('strf', strf)]));
        } else {
            var a = spec.audio, wf = waveFormat(a, spec.sr), tm = audioTiming(a, spec.sr, wf);
            var strhA = concat([strBytes('auds'), new Uint8Array(4),
                le([[4, 0], [2, 0], [2, 0], [4, 0], [4, tm.scale], [4, tm.rate], [4, spec.audioStart || 0], [4, spec.audioLength || 0],
                    [4, spec.maxAudio], [4, 0xffffffff], [4, tm.sampleSize], [2, 0], [2, 0], [2, 0], [2, 0]])]);
            streamsHdr.push(list('strl', [ck('strh', strhA), ck('strf', wf.bytes)]));
        }
    });
    var avih = le([[4, Math.round(1e6 / spec.fps)], [4, spec.maxBytesPerSec], [4, 0], [4, (spec.withIndex ? 0x10 : 0) | 0x100 | 0x800],
        [4, spec.frames], [4, 0], [4, order.length], [4, spec.maxChunk], [4, v.width], [4, v.height], [4, 0], [4, 0], [4, 0], [4, 0]]);
    return list('hdrl', [ck('avih', avih)].concat(streamsHdr));
}

/* ── donor ───────────────────────────────────────────────────────────────── */

/**
 * The donor's stream descriptions, verbatim: another clip from the same camera
 * knows the rates and the codec configuration exactly.
 */
async function headerFrom(referenceFile) {
    var b = await readAt(referenceFile, 0, 1 * MB);
    if (b.length < 64 || fcc(b, 0) !== 'RIFF' || fcc(b, 8) !== 'AVI ') return null;
    var p = 12, hdrl = null;
    while (p + 12 <= b.length) {
        var id = fcc(b, p), sz = u32(b, p + 4);
        if (id === 'LIST' && fcc(b, p + 8) === 'hdrl') { hdrl = { at: p + 12, end: Math.min(b.length, p + 8 + sz) }; break; }
        p += 8 + sz + (sz & 1);
    }
    if (!hdrl) return null;
    var out = { streams: [], avih: null };
    p = hdrl.at;
    while (p + 8 <= hdrl.end) {
        var id2 = fcc(b, p), sz2 = u32(b, p + 4);
        if (id2 === 'avih') out.avih = { usPerFrame: u32(b, p + 8), width: u32(b, p + 40), height: u32(b, p + 44) };
        if (id2 === 'LIST' && fcc(b, p + 8) === 'strl') {
            var q = p + 12, qe = p + 8 + sz2, s = {};
            while (q + 8 <= qe) {
                var cid = fcc(b, q), csz = u32(b, q + 4);
                if (cid === 'strh') {
                    s.type = fcc(b, q + 8); s.handler = fcc(b, q + 12);
                    s.scale = u32(b, q + 28); s.rate = u32(b, q + 32); s.start = u32(b, q + 36); s.sampleSize = u32(b, q + 52);
                    s.strh = b.slice(q + 8, q + 8 + csz);
                }
                if (cid === 'strf') {
                    s.strf = b.slice(q + 8, q + 8 + csz);
                    if (s.type === 'vids' || s.type === 'iavs') { s.width = u32(b, q + 12); s.height = Math.abs(new DataView(b.buffer, b.byteOffset).getInt32(q + 16, true)); s.fourcc = fcc(b, q + 24); }
                    if (s.type === 'auds') { s.tag = u16(b, q + 8); s.ch = u16(b, q + 10); s.sr = u32(b, q + 12); s.align = u16(b, q + 20); }
                }
                q += 8 + csz + (csz & 1);
            }
            out.streams.push(s);
        }
        p += 8 + sz2 + (sz2 & 1);
    }
    if (!out.streams.length) return null;
    /* Decoder configuration that lives in the FIRST FRAME rather than the
       header: MPEG-4 VOL (Xvid/DivX write it once), H.264 SPS/PPS (some writers
       send them once). A damaged file that lost its first frame lost these too. */
    for (var i = 12; i + 12 < b.length; i++) {
        if (streamKind(b, i) !== K_VIDEO) continue;
        var vz = u32(b, i + 4);
        if (vz < 8 || vz > MAX_CHUNK) continue;
        var f = i + 8 + vz <= b.length ? b.subarray(i + 8, i + 8 + vz) : await readAt(referenceFile, i + 8, vz);
        out.config = decoderConfig(f);
        break;
    }
    return out;
}

/** The decoder-configuration prefix of a frame: MPEG-4 VOS..VOL (up to the
 *  first VOP) or H.264 SPS+PPS NALs (Annex B, with start codes). */
function decoderConfig(f) {
    var n = Math.min(f.length, 4096), i, out = null;
    for (i = 0; i + 4 < n; i++) {
        if (f[i] !== 0 || f[i + 1] !== 0 || f[i + 2] !== 1) continue;
        var c = f[i + 3];
        if (c >= 0x20 && c <= 0x2f) {                  // VOL: take from the first start code up to the VOP
            var from = 0;
            for (var k = i; k >= 0; k--) if (f[k] === 0 && f[k + 1] === 0 && f[k + 2] === 1 && (f[k + 3] === 0xb0 || f[k + 3] === 0xb5 || f[k + 3] < 0x20)) from = k;
            for (var e = i + 4; e + 3 < n; e++) if (f[e] === 0 && f[e + 1] === 0 && f[e + 2] === 1 && (f[e + 3] === 0xb6 || f[e + 3] === 0xb3)) return { kind: 'vol', bytes: f.slice(from, e) };
            return { kind: 'vol', bytes: f.slice(from, n) };
        }
    }
    var nals = [];
    for (i = 0; i + 4 < n; i++) {
        if (f[i] !== 0 || f[i + 1] !== 0 || f[i + 2] !== 1) continue;
        var t = f[i + 3] & 0x1f;
        if (t === 7 || t === 8) {
            var end = n;
            for (var e2 = i + 3; e2 + 3 < n; e2++) if (f[e2] === 0 && f[e2 + 1] === 0 && (f[e2 + 2] === 1 || (f[e2 + 2] === 0 && f[e2 + 3] === 1))) { end = e2; break; }
            nals.push(new Uint8Array([0, 0, 0, 1]), f.slice(i + 3, end));
        }
        if (t === 5 || t === 1) break;
    }
    if (nals.length) out = { kind: 'h264ps', bytes: concat(nals) };
    return out;
}

/* ── rebuild ─────────────────────────────────────────────────────────────── */

/**
 * Rebuild. Returns the candidate file and what it believes; `opts.fps` /
 * `opts.sampleRate` override the solver (the page's "it plays too fast" answer),
 * `opts.reference` takes the donor lane.
 */
async function rebuild(file, opts) {
    var o = opts || {};
    var t0 = Date.now();
    var x = await examine(file, o);
    if (!x) return null;
    var w = x.walked, t = w.table, video = x.video, audio = x.audio;
    var donor = o.reference ? await headerFrom(o.reference) : null;
    var dv = null, da = null;
    if (donor) {
        dv = donor.streams.filter(function (s) { return s.type === 'vids' || s.type === 'iavs'; })[0] || null;
        da = donor.streams.filter(function (s) { return s.type === 'auds'; })[0] || null;
    }
    /* Frames we cannot name, or H.264 whose parameter sets went with the head:
       not a failure to hide behind `null` — the page has to ASK for a sample
       clip, and say what is missing. */
    var need = !dv ? needOf(video, w.family) : null;
    if (need) {
        return { blob: null, needsDonor: need, at: w.at, end: w.end, chunks: t.n, family: w.family,
                 video: { family: w.family, frames: video.frames, width: video.width, height: video.height },
                 audio: audio ? { codec: audio.codec } : null, endReason: w.endReason, ms: Date.now() - t0 };
    }
    if (!audio && da) {
        /* Raw AAC and the like name nothing themselves: the donor names them,
           and the interleave is measured again with that description so the
           sound is placed, not just appended. */
        x = await examine(file, Object.assign({}, o, { walked: w, audio: audioFromDonor(da) }));
        audio = x.audio;
    }

    var fps = o.fps || (dv ? dv.rate / dv.scale : x.rates.fps) || 30;
    /* A frame rate the person chose moves the sample rate with it: what the file
       measures is samples PER FRAME (the interleave), so 15 fps instead of 30
       is half the sample rate too — keeping the solver's rate would let the
       sound drift off the picture by half its length. Not for audio whose
       frames name their own rate (MP3, AC-3, AAC): there the rate is a fact. */
    var srFollows = o.fps && !o.sampleRate && !da && audio && !audio.exactRate && x.interleave.R
        ? snapSr(x.interleave.R * o.fps, 0.02) : null;
    var sr = o.sampleRate || (da && da.sr) || srFollows || x.rates.sr || (audio && audio.sr) || null;

    /* Audio we can declare: sniffed with a known rate, or described by the
       donor verbatim (raw AAC and the like, which name nothing themselves). */
    var audioSids = Object.keys(x.streams).map(Number).filter(function (q) { return x.streams[q].kind === K_AUDIO; });
    var aSid = audio ? audio.sid : (audioSids.length ? audioSids[0] : -1);
    var useAudio = aSid >= 0 && ((!!audio && !!sr) || !!da);

    /* Where the picture can start: the first frame a decoder can take. */
    var keyIdx = x.keyIdx >= 0 ? x.keyIdx : -1;
    if (keyIdx < 0) {
        for (var s = 0; s < t.n; s++) if (t.sid[s] === video.sid && t.size[s] > 0) { keyIdx = s; break; }
    }
    /* The donor's decoder configuration goes in front of that frame when the
       file lost its own (VOL / SPS+PPS written once, in the first frame). */
    var inject = null;
    if (donor && donor.config && keyIdx >= 0) {
        var kf = await readAt(file, t.off[keyIdx] + 8, Math.min(t.size[keyIdx], 4096)), own = decoderConfig(kf);
        if (!own || own.kind !== donor.config.kind) inject = donor.config.bytes;
    }

    /* Where the audio starts, in samples of the region's audio. */
    var sync = { want: 0, rule: 'none' };
    if (useAudio && audio && sr) sync = audioStartFor(x, fps, sr, keyIdx);
    var skipSamples = 0, delaySamples = 0;
    if (sync.want > 0) skipSamples = Math.round(sync.want * sr);
    else if (sync.want < 0) delaySamples = Math.round(-sync.want * sr);

    /* ── assemble movi: kept chunks as slices of the source, runs merged ── */
    var parts = [], idx = [], moviBytes = 4, frames = 0, audioUnits = 0, audioChunks = 0, maxV = 0, maxA = 0;
    var runStart = -1, runEnd = -1;
    function flush() { if (runStart >= 0) { parts.push(file.slice(runStart, runEnd)); runStart = runEnd = -1; } }
    function keep(i, outSid) {
        var off = t.off[i], sz = t.size[i], len = 8 + sz + (sz & 1);
        if (outSid !== t.sid[i]) {
            flush();
            var h = new Uint8Array(8);
            h[0] = 0x30 + Math.floor(outSid / 10); h[1] = 0x30 + outSid % 10; h[2] = hdrByte(t, i, 2); h[3] = hdrByte(t, i, 3);
            new DataView(h.buffer).setUint32(4, sz, true);
            parts.push(h, file.slice(off + 8, off + 8 + sz + (sz & 1)));
        } else if (runStart >= 0 && runEnd === off) runEnd = off + len;
        else { flush(); runStart = off; runEnd = off + len; }
        return len;
    }
    /* Streams in the output, by sid: video first as the rest of the world
       expects, and audio (if declared) right after it. */
    var dvi = video.kind === K_DV;
    var order = [{ sid: video.sid, type: dvi ? 'dvi' : 'video', out: 0 }];
    if (useAudio && aSid >= 0) order.push({ sid: aSid, type: 'audio', out: 1 });
    var outSidOf = {}; order.forEach(function (s) { outSidOf[s.sid] = s.out; });

    var skipLeft = skipSamples, trimmedFirst = null, firstAudioOff = -1, firstAudioCut = 0;
    for (var i = 0; i < t.n; i++) {
        var sid = t.sid[i];
        if (!(sid in outSidOf)) continue;
        if (sid === video.sid) {
            if (i < keyIdx) continue;
            var start = moviBytes;
            if (inject && i === keyIdx) {
                flush();
                var kbody = await readAt(file, t.off[i] + 8, t.size[i]), kb = concat([inject, kbody]);
                var kh = new Uint8Array(8);
                kh[0] = 0x30; kh[1] = 0x30 + outSidOf[sid]; kh[2] = 0x64; kh[3] = 0x63;
                new DataView(kh.buffer).setUint32(4, kb.length, true);
                parts.push(kh, kb); if (kb.length & 1) parts.push(new Uint8Array(1));
                idx.push([outSidOf[sid], K_VIDEO, 0x10, start, kb.length]);
                moviBytes += 8 + kb.length + (kb.length & 1);
                frames++; if (kb.length > maxV) maxV = kb.length;
                continue;
            }
            moviBytes += keep(i, outSidOf[sid]);
            idx.push([outSidOf[sid], t.kind[i], t.key[i] === 1 ? 0x10 : 0, start, t.size[i]]);
            frames++; if (t.size[i] > maxV) maxV = t.size[i];
            continue;
        }
        /* audio */
        if (sync.rule === 'none' && i < keyIdx) continue;
        var sz = t.size[i], n = audio ? samplesIn(audio, sz) : 0;
        if (skipLeft > 0 && audio) {
            if (n <= skipLeft) { skipLeft -= n; continue; }
            /* The start falls inside this chunk. PCM cuts at the sample; ADPCM
               at the nearest block (blocks decode alone); frame-coded audio
               drops the chunk and says the rest as a start delay, which its
               byte timing can express exactly. */
            if (audio.frameCoded) { delaySamples += n - skipLeft; skipLeft = 0; continue; }
            if (!audio.pcm) {
                var blocks = Math.round(skipLeft / audio.spb);
                skipLeft = blocks * audio.spb;
                if (skipLeft >= n) { skipLeft = 0; continue; }
            }
            if (skipLeft > 0) {
                /* cut inside this chunk at a block boundary */
                var cutBytes = audio.pcm ? skipLeft * audio.align : (skipLeft / audio.spb) * audio.align;
                flush();
                var body = await readAt(file, t.off[i] + 8 + cutBytes, sz - cutBytes);
                var hdr = new Uint8Array(8);
                hdr[0] = 0x30; hdr[1] = 0x30 + outSidOf[sid]; hdr[2] = 0x77; hdr[3] = 0x62;
                new DataView(hdr.buffer).setUint32(4, body.length, true);
                parts.push(hdr, body); if (body.length & 1) parts.push(new Uint8Array(1));
                idx.push([outSidOf[sid], K_AUDIO, 0x10, moviBytes, body.length]);
                moviBytes += 8 + body.length + (body.length & 1);
                audioUnits += body.length; audioChunks++; if (body.length > maxA) maxA = body.length;
                trimmedFirst = { cutSamples: skipLeft };
                if (firstAudioOff < 0) { firstAudioOff = t.off[i]; firstAudioCut = skipLeft; }
                skipLeft = 0;
                continue;
            }
        }
        var startA = moviBytes;
        if (firstAudioOff < 0) { firstAudioOff = t.off[i]; firstAudioCut = 0; }
        moviBytes += keep(i, outSidOf[sid]);
        idx.push([outSidOf[sid], K_AUDIO, 0x10, startA, sz]);
        audioUnits += sz; audioChunks++; if (sz > maxA) maxA = sz;
    }
    flush();
    if (!frames) return null;

    /* ── header, idx1 and the file ── */
    var bytesTotal = moviBytes + 16 * idx.length + 8;
    var withIndex = bytesTotal < MAX_RIFF_BYTES - 65536;
    var aTiming = null, audioStart = 0, audioLength = 0;
    if (useAudio && audio && sr) {
        var wf = waveFormat(audio, sr);
        aTiming = audioTiming(audio, sr, wf);
        audioStart = delaySamples ? Math.round(delaySamples / sr * aTiming.rate / aTiming.scale) : 0;
        audioLength = aTiming.sampleSize ? Math.round(audioUnits / aTiming.sampleSize) : audioChunks;   // VBR: one unit a chunk
    }
    var hdrl;
    if (donor && dv) hdrl = donorHdrl(donor, order, frames, audioStart);
    else hdrl = buildHeader({ video: video, audio: audio, fps: fps, sr: sr, order: order, frames: frames,
        maxVideo: maxV, maxAudio: maxA, maxChunk: Math.max(maxV, maxA) + 8, withIndex: withIndex,
        maxBytesPerSec: Math.round((moviBytes / Math.max(1, frames)) * fps), audioStart: audioStart, audioLength: audioLength });

    var moviHead = new Uint8Array(12);
    moviHead.set(strBytes('LIST'), 0); moviHead.set(strBytes('movi'), 8);
    new DataView(moviHead.buffer).setUint32(4, withIndex ? moviBytes : 0, true);
    var idx1 = null;
    if (withIndex) {
        idx1 = new Uint8Array(8 + 16 * idx.length);
        idx1.set(strBytes('idx1'), 0);
        var dv1 = new DataView(idx1.buffer);
        dv1.setUint32(4, 16 * idx.length, true);
        for (var e = 0; e < idx.length; e++) {
            var r = idx[e], o0 = 8 + 16 * e;
            idx1[o0] = 0x30; idx1[o0 + 1] = 0x30 + r[0];
            idx1[o0 + 2] = r[1] === K_AUDIO ? 0x77 : (r[1] === K_DV ? 0x5f : 0x64);
            idx1[o0 + 3] = r[1] === K_AUDIO ? 0x62 : (r[1] === K_DV ? 0x5f : 0x63);
            dv1.setUint32(o0 + 4, r[2], true);
            dv1.setUint32(o0 + 8, r[3], true);
            dv1.setUint32(o0 + 12, r[4], true);
        }
    }
    var riffBody = 4 + hdrl.length + 12 - 4 + moviBytes + (idx1 ? idx1.length : 0);
    var riff = new Uint8Array(12);
    riff.set(strBytes('RIFF'), 0); riff.set(strBytes('AVI '), 8);
    new DataView(riff.buffer).setUint32(4, withIndex ? riffBody : 0, true);
    var header = concat([riff, hdrl, moviHead]);
    var blobParts = [header].concat(parts);
    if (idx1) blobParts.push(idx1);

    var videoOut = Object.assign({}, video, { frames: frames, fps: fps, keyStartAt: t.off[keyIdx], framesBeforeKey: countBefore(t, video.sid, keyIdx),
                                              fourcc: video.fourcc || (dv && dv.fourcc) || null });
    return {
        blob: new Blob(blobParts, { type: 'video/x-msvideo' }),
        lane: donor && dv ? 'reference' : 'sniff',
        at: w.at, end: w.end, headerBytes: header.length, withIndex: withIndex,
        video: videoOut,
        audio: useAudio ? Object.assign({}, audio || {}, { sr: sr, delaySamples: delaySamples, skipSamples: skipSamples }) : null,
        audioDropped: x.streams && aSid >= 0 && !useAudio ? (audio ? 'no-rate' : 'unknown-codec') : null,
        rates: o.fps || o.sampleRate ? Object.assign({}, x.rates, { fps: fps, sr: sr, source: 'user' }) : (donor && dv ? { fps: fps, sr: sr, source: 'reference', ambiguous: false, alternatives: [] } : x.rates),
        sync: sync, holes: w.holes.length, slops: w.slops, endReason: w.endReason,
        family: w.family, chunks: t.n, ms: Date.now() - t0,
        /* for tests: which source bytes the output's first audio sample came from */
        debug: { firstAudioOff: firstAudioOff >= 0 ? firstAudioOff : null, firstAudioCut: firstAudioCut,
                 samplesPerByte: audio ? (audio.pcm ? 1 / audio.align : (audio.frameCoded ? audio.spf / audio.frameBytes : audio.spb / audio.align)) : 0 },
    };
}

/**
 * What only a sample clip can supply, or null when the frames are enough.
 *   'h264-params'  H.264 whose SPS/PPS went with the head (sent once, or kept
 *                  in the lost header as with AVCC-framed streams)
 *   'mpeg4-vol'    MPEG-4 Part 2 whose VOL lived in the lost header (DivX 5 and
 *                  others keep it in strf extradata, not in the frames:
 *                  measured on two of the corpus files, 0 of 50 frames carry it)
 *   'video-codec'  frames that do not name their codec at all (WMV3/VC-1,
 *                  MS-MPEG4, unknown)
 */
function needOf(video, family) {
    if (video.fourcc && !(family === 'h264' && video.paramsMissing)) return null;
    if (family === 'h264' || family === 'h264-avcc') return 'h264-params';
    if (family === 'mpeg4') return 'mpeg4-vol';
    return 'video-codec';
}

/** An audio description from the donor's WAVEFORMATEX, for streams the bytes cannot name. */
function audioFromDonor(da) {
    var tag = da.tag, ch = da.ch || 1;
    if (tag === 1 || tag === 3 || tag === 6 || tag === 7) {
        var bits = da.strf.length >= 16 ? u16(da.strf, 14) : 16, fmt = tag === 7 ? 'mulaw' : tag === 6 ? 'alaw' : tag === 3 ? 'f32le' : bits === 8 ? 'u8' : bits === 24 ? 's24le' : 's16le';
        return pcmResult(fmt, ch, 0, false);
    }
    if (tag === 0x11 || tag === 0x02) {
        var spb = da.strf.length >= 20 ? u16(da.strf, 18) : 0;
        return { codec: tag === 0x11 ? 'adpcm_ima_wav' : 'adpcm_ms', tag: tag, align: da.align, ch: ch, spb: spb, bits: 4, exactRate: false, frameCoded: false };
    }
    /* Frame-coded without a sniff (raw AAC): one frame a chunk, as every AVI
       muxer writes it. */
    var spf = tag === 0xff || tag === 0x706d || tag === 0x1600 ? 1024 : tag === 0x2000 ? 1536 : 1152;
    return { codec: 'donor-' + tag.toString(16), tag: tag, sr: da.sr, ch: ch, spf: spf, frameBytes: 1, exactRate: true, frameCoded: true, oneFramePerChunk: true };
}

function hdrByte(t, i, k) {
    var kind = t.kind[i];
    if (kind === K_AUDIO) return k === 2 ? 0x77 : 0x62;
    if (kind === K_DV) return 0x5f;
    return k === 2 ? 0x64 : 0x63;
}
function countBefore(t, sid, upTo) { var n = 0; for (var i = 0; i < upTo && i < t.n; i++) if (t.sid[i] === sid) n++; return n; }

/** hdrl from the donor's strl lists, with this file's counts. */
function donorHdrl(donor, order, frames, audioStart) {
    var strls = [];
    order.forEach(function (s) {
        var d = donor.streams.filter(function (x) { return s.type === 'audio' ? x.type === 'auds' : (x.type === 'vids' || x.type === 'iavs'); })[0];
        var strh = d.strh.slice();
        var dvw = new DataView(strh.buffer);
        if (s.type !== 'audio') { dvw.setUint32(28, 0, true); dvw.setUint32(32, frames, true); }
        else { dvw.setUint32(28, audioStart || 0, true); dvw.setUint32(32, 0, true); }
        strls.push(list('strl', [ck('strh', strh), ck('strf', d.strf)]));
    });
    var dv0 = donor.streams.filter(function (x) { return x.type === 'vids' || x.type === 'iavs'; })[0];
    var avih = le([[4, donor.avih ? donor.avih.usPerFrame : Math.round(1e6 * dv0.scale / dv0.rate)], [4, 0], [4, 0], [4, 0x10 | 0x100 | 0x800],
        [4, frames], [4, 0], [4, order.length], [4, 0], [4, dv0.width || 0], [4, dv0.height || 0], [4, 0], [4, 0], [4, 0], [4, 0]]);
    return list('hdrl', [ck('avih', avih)].concat(strls));
}

export { walk, examine, rebuild, headerFrom, bodyStart, solveRates, sniffAudio, familyOf, isKey, h264Sps, m4vVol, jpegDims,
         mpaHeader, ac3Header, adpcmLayout, audioStartFor, buildHeader, needOf };

/* The blade's inline JS reaches this by name, the way it reaches MediaLocate. */
if (typeof window !== 'undefined') {
    window.AviSurgeon = {
        walk: walk, examine: examine, rebuild: rebuild, headerFrom: headerFrom, bodyStart: bodyStart,
        solveRates: solveRates, version: '1.1.0',
    };
}
